import { Dispatch, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Modal } from '../../../UIPackage/ModalModule/components/Modal';
import { ModalHeader } from '../../../UIPackage/ModalModule/components/ModalHeader';
import { ModalBody } from '../../../UIPackage/ModalModule/components/ModalBody';
import { ModalActions } from '../../../UIPackage/ModalModule/components/ModalActions';
import { Button } from '../../../UIPackage/MainModule/components/Button';
import { InfoBlock } from '../../../CorePackage/MainModule/components/InfoBlock';
import { ISelectOption, SelectInput } from '../../../UIPackage/MainModule/components/SelectInput';
import { Row } from '../../../UIPackage/FormModule/components/Row';
import { FieldBlock } from '../../../UIPackage/FormModule/components/FieldBlock';
import { REQUIRED_ERROR } from '../../../CorePackage/MainModule/constants/validationErrors';
import { TextInput } from '../../../UIPackage/MainModule/components/TextInput';
import { IPassportGroupReason } from '../../../APIPackage/PassportGroupsModule/interfaces/IPassportGroupReason';
import { IPassportGroupApplyRequest } from '../../../APIPackage/PassportGroupsModule/interfaces/IPassportGroupApplyRequest';
import {
  passportGroupApplyAction,
  passportGroupSetApplySuccessAction,
} from '../../../PassportGroupPackage/MainModule/actions/passportGroupActions';
import { RootActions } from '../../../CorePackage/MainModule/types/RootActions';
import { passportGroupApplySuccessSelector } from '../../../PassportGroupPackage/MainModule/selectors/passportGroupSelector';
import { addNoticeAction } from '../../../CorePackage/NotificationModule/actions/NoticeActions';
import { NoticeTypesEnum } from '../../../CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from '../../../CorePackage/NotificationModule/enums/MessageTypesEnum';
import { catalogGet } from '../../../CorePackage/CatalogsModule/actions/catalogsActions';
import { CommonCatalogsNamesEnum } from '../../../CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { getPassportMassDeleteReasonSelectOptions } from '../../../CorePackage/CatalogsModule/selectors/catalogsSelectors';

interface IPassportMassDeletionModalProps {
  groupId: number;
  onClose: () => void;
}

const newReasonSelectOption: ISelectOption = {
  id: 'new',
  label: 'Создать новое',
};

export const PassportMassDeletionModal = ({ groupId, onClose }: IPassportMassDeletionModalProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const massDeleteReasonSelectOptions = useSelector(getPassportMassDeleteReasonSelectOptions);
  const isMassDeleteSuccess = useSelector(passportGroupApplySuccessSelector);
  const [reasonNewValue, setReasonNewValue] = useState<string>('');
  const [reasonValue, setReasonValue] = useState<ISelectOption>();
  const [reasonNewError, setReasonNewError] = useState<string>();
  const [reasonError, setReasonError] = useState<string>();
  const [isShowErrors, setIsShowErrors] = useState(false);

  useEffect(() => {
    setReasonNewValue('');
  }, [reasonValue, setReasonNewValue]);

  useEffect(() => {
    dispatch(catalogGet(CommonCatalogsNamesEnum.PASSPORT_MASS_DELETE_REASON, true));
  }, [dispatch]);

  useEffect(() => {
    setReasonError(!reasonValue ? REQUIRED_ERROR : undefined);
    setReasonNewError(reasonValue?.id === 'new' && !reasonNewValue ? REQUIRED_ERROR : undefined);
  }, [reasonValue, reasonNewValue, setReasonError, setReasonNewError]);

  useEffect(() => {
    if (isMassDeleteSuccess) {
      dispatch(passportGroupSetApplySuccessAction(false));
      dispatch(
        addNoticeAction({
          renderContent: 'Запущен фоновый процесс удаления мероприятий группы',
          noticeType: NoticeTypesEnum.SUCCESS,
          messageType: MessageTypesEnum.PASSPORT_GROUP,
        }),
      );
      onClose();
    }
  }, [dispatch, isMassDeleteSuccess, onClose]);

  const handleSubmit = useCallback(() => {
    setIsShowErrors(true);

    if (reasonError || reasonNewError) {
      return;
    }

    const reason: IPassportGroupReason =
      typeof reasonValue?.id === 'number'
        ? { id: reasonValue.id, name: reasonValue.label }
        : { id: null, name: reasonNewValue };

    const data: IPassportGroupApplyRequest = {
      reason,
      reasonFile: null,
    };

    dispatch(passportGroupApplyAction(groupId, data));
  }, [setIsShowErrors, reasonError, reasonNewError, reasonValue, reasonNewValue, dispatch, groupId]);

  return (
    <Modal visible onClose={onClose}>
      <ModalHeader title="Массовое удаление мероприятий" />
      <ModalBody size="content">
        <InfoBlock iconVerticalPosition="top" marginBottom="1.25rem">
          Внимание! Мероприятия, решения и проверки будут переведены в статус «Удалено». Если Вы уверены, что хотите
          продолжить, укажите Основания массового удаления и нажмите «Подтвердить». Если Вы не хотите удалять
          мероприятия и проверки, нажмите «Назад»
        </InfoBlock>
        <Row>
          <FieldBlock label="Основание массового удаления">
            <SelectInput
              isSearchable
              onChange={setReasonValue}
              value={reasonValue}
              options={[newReasonSelectOption, ...massDeleteReasonSelectOptions]}
              error={isShowErrors ? reasonError : undefined}
              invalid={isShowErrors && !!reasonError}
            />
          </FieldBlock>
        </Row>
        {reasonValue?.id === 'new' && (
          <Row>
            <FieldBlock>
              <TextInput
                value={reasonNewValue}
                onChange={e => setReasonNewValue(e.target.value)}
                error={isShowErrors ? reasonNewError : undefined}
                invalid={isShowErrors && !!reasonNewError}
              />
            </FieldBlock>
          </Row>
        )}
      </ModalBody>
      <ModalActions>
        <Button text="Подтвердить" onClick={handleSubmit} size="medium" color="primary" />
        <Button text="Назад" onClick={onClose} size="medium" />
      </ModalActions>
    </Modal>
  );
};
